import DesignTokens from '@tableau/design-tokens';
import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget } from '@tableau/widgets';
import React, { CSSProperties } from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { applyColor }from '~src/style/ColorService';

import { GraphDatabase, GraphTable, mapGraphTypeToContentInfoForActions } from '../../../catalog/models/mappers/DatabaseAndTableMapper';
import { Formatters, Messages } from '../../../codegen/Localize';
import { DisplayMode } from '../../../sessionManagement/serverApi/types/ISessionInfo';
import { ExplorableItemType } from '../../models/ContentType';
import { IExplorableItemTypeInfo } from '../../models/resourceInfoParts';
import { getDisplaySortDropdownForContentTypes } from '../../models/sort/SortOptionDefinitions';
import { isProjectSpace, isSiteSpace, isSiteUserContentSubspace, IsWorkbookDatasourceSubspaceTab, SpaceInfo } from '../../state/SpaceInfo';
import { ItemsSet } from '../../utils/ItemsSet';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { AdvancedFilterPanelToggleButton } from './AdvancedFilterPanelToggleButton';
import { CARD_RESIZE_SLIDER_LABEL_ID, ConnectedCardResizeSlider } from './CardResizeSlider';
import { DatasourceViewType, DatasourceViewTypeDropdown, DatasourceViewTypeInfo } from './DatasourceViewTypeDropdown';
import { ExplorerStyles } from './ExplorerStyles';
import { ExplorerViewModeSwitcher } from './ExplorerViewModeSwitcher';
import { SortByDropdown } from './SortByDropdown';

export interface ExplorerToolbarOwnProps {
  readonly skipHorizontalMargin?: boolean;
}

export interface ExplorerToolbarStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly itemCount: number;
  readonly supportedDisplayModes: ReadonlyArray<DisplayMode>;
  readonly currentViewMode: DisplayMode;
  readonly changeViewMode: (viewMode: DisplayMode) => void;
  readonly disableButtonsOnRight?: boolean;
  readonly contentSpecificControls?: ContentSpecificControls;
  readonly hideContentSpecificControlsInEmptyState?: boolean;

  // item selection
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly isSelectionBusy: boolean;
  readonly hideSelection: boolean;

  // filtering & sorting
  readonly filtersApplied: boolean;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly contentFilterControl?: JSX.Element;
  readonly hasTextSearchParameter: boolean; // should this just be an array of filters (and below)
  readonly showAdvancedFilterButton: boolean;
  readonly filterPanelShown: boolean;
  readonly advancedFilterButtonClickHandler: () => void;
  readonly contentSort: ReadonlyArray<IOrderClause>;

  // datasource specific filtering
  readonly showDatasourceViewTypeDropDown: boolean;
  readonly datasourceViewTypeSelected: (viewType: DatasourceViewTypeInfo) => void;
}

export interface ExplorerToolbarDispatchProps {
  readonly selectAll: () => void;
  readonly clearAllSelections: () => void;
  readonly contentSortSelected: (order: ReadonlyArray<IOrderClause>) => void;
}

export type ExplorerToolbarProps = ExplorerToolbarOwnProps & ExplorerToolbarStateProps & ExplorerToolbarDispatchProps;

export type ContentSpecificControlPosition = 'beginning' | 'end';
export type ContentSpecificControls = { [P in ContentSpecificControlPosition]?: JSX.Element };

export class ExplorerToolbar extends React.PureComponent<ExplorerToolbarProps> {

  private memoizedGetDerivedPropsFromSelectedItems = defaultMemoize(getDerivedPropsFromSelectedItems);
  private selectAllButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

  public render(): JSX.Element {

    const { selectedItemsString, selectedItemsArray } = this.memoizedGetDerivedPropsFromSelectedItems(this.props.selectedItems);

    // currently we leave most of the toolbar almost always enabled.
    // Prior logic was this.props.isSelectionBusy || this.props.itemCount === 0
    // but this lead to confusing situations with advanced filters and content type
    const disabled = this.props.disableButtonsOnRight || false;

    /** Currently content items that are selected in the explorer aren't _really_ IExplorableItemTypeInfo
     *  See the doc comment on mapGraphTypeToIContentInfo and hopefully this can be removed some day
     */
    const mappedSelectedItems = selectedItemsArray.map(
      t => t.contentType === 'database' || t.contentType === 'table'
        ? mapGraphTypeToContentInfoForActions(t as GraphDatabase | GraphTable)
        : t
    );



    return (
      <div data-tb-test-id={ExplorerToolbarTestIds.Toolbar}>
        <div className={ExplorerStyles.Toolbar + (this.props.skipHorizontalMargin ? (' ' + ExplorerStyles.NoHorizontalWhiteSpace) : '')} data-tb-test-id={'tb-spaces-toolbar'}>
          <div className={ExplorerStyles.ToolbarButtonGroup}>
            {this.props.contentSpecificControls &&
              (this.props.hideContentSpecificControlsInEmptyState ? this.props.itemCount > 0 : true) &&
              this.props.contentSpecificControls.beginning}
            {this.props.hideSelection ||
              <React.Fragment>
                <ButtonWidget
                  buttonType={ButtonType.LowEmphasis}
                  buttonRef={this.selectAllButtonRef}
                  children={Messages.selectAll()}
                  showSpinner={this.props.isSelectionBusy}
                  disabled={this.props.itemCount === 0}
                  onClick={this.props.selectAll}
                  testId={ExplorerToolbarTestIds.SelectAllButton}
                  style={ExplorerStyles.ToolbarButtonWidthPadding} />

                <ButtonWidget
                  buttonType={ButtonType.LowEmphasis}
                  children={Messages.clearAll()}
                  disabled={this.props.isSelectionBusy}
                  onClick={() => {
                    this.props.clearAllSelections();
                    this.selectAllButtonRef.current?.focus();
                  }}
                  testId={ExplorerToolbarTestIds.DeselectAllButton}
                  style={{ ...ExplorerStyles.ToolbarButtonWidthPadding, ...this.calculateVisiblityStyle(selectedItemsArray.length > 0 || this.props.isSelectionBusy) }} />
                <span className={ExplorerStyles.ToolbarButtonDivider} style={this.calculateVisiblityStyle(selectedItemsArray.length > 0 || this.props.isSelectionBusy)} />
                {this.props.isSelectionBusy &&
                  <div className={ExplorerStyles.ToolbarButtonGroup}>
                    <ActivitySpinnerWidget testId={ExplorerToolbarTestIds.BusySpinner} spinnerSize={ActivitySpinnerSize.Small} />
                    <span>{Messages.selecting()}</span>
                  </div>
                }

                <span role="status" aria-live="polite" data-tb-test-id={ExplorerToolbarTestIds.SelectedItemsCount} style={this.calculateVisiblityStyle(selectedItemsArray.length > 0 && !this.props.isSelectionBusy && !!selectedItemsString)}>
                  {selectedItemsString}
                </span>
                <div style={this.calculateVisiblityStyle(selectedItemsArray.length > 0 && !this.props.isSelectionBusy)}>
                  <ActionsMenu targetItems={mappedSelectedItems} useIcon={false} />
                </div>
              </React.Fragment>
            }
          </div>

          <div className={ExplorerStyles.ToolbarButtonGroup}>

            {this.dataSourceViewTypeDropdown(disabled)}

            {FeatureFlags.cardResizerEnabled &&
              this.props.supportedDisplayModes.indexOf(this.props.currentViewMode) !== -1 &&
              this.props.currentViewMode === 'thumbnail' &&
              <React.Fragment>
                <span id={CARD_RESIZE_SLIDER_LABEL_ID} className={ExplorerStyles.toolbarDropdownLabelClass(disabled)}>{Messages.resizeCard()}</span>
                <ConnectedCardResizeSlider />
              </React.Fragment>}

            {this.props.supportedDisplayModes.length > 1 &&
              <ExplorerViewModeSwitcher
                disabled={disabled}
                currentViewMode={this.props.currentViewMode}
                changeViewMode={this.changeViewMode} />}

            {this.shouldShowContentSort() &&
              <SortByDropdown
                disabled={disabled}
                order={this.props.contentSort}
                orderSelected={this.props.contentSortSelected}
                contentTypes={this.props.contentFilter}
                spaceInfo={this.props.spaceInfo}
                hasTextSearchValue={this.props.hasTextSearchParameter}
              />
            }

            {this.props.contentFilterControl}

            {this.props.showAdvancedFilterButton &&
              <AdvancedFilterPanelToggleButton
                disabled={disabled}
                advancedFilterButtonClickHandler={this.props.advancedFilterButtonClickHandler}
                filtersApplied={this.props.filtersApplied}
                filterPanelShown={this.props.filterPanelShown}
              />}
            {this.props.contentSpecificControls && this.props.contentSpecificControls.end}
          </div>
        </div>
      </div>
    );
  }

  private calculateVisiblityStyle(shouldBeVisible: boolean): CSSProperties {
    return {
      visibility: shouldBeVisible ? 'visible' : 'hidden'
    };
  }

  private shouldShowContentSort() {
    return this.props.contentFilter?.length > 0 && getDisplaySortDropdownForContentTypes(this.props.contentFilter, this.props.spaceInfo);
  }

  private showDatasourceViewTypeDropDown = () => {
    const isShowingOnlyDatasourcesOnSpecificPages = (isProjectSpace(this.props.spaceInfo) || isSiteSpace(this.props.spaceInfo) || isSiteUserContentSubspace(this.props.spaceInfo))
      && this.props.contentFilter.length === 1
      && (this.props.contentFilter[0] === 'datasource'
        || this.props.contentFilter[0] === 'dataconnection');

    return isShowingOnlyDatasourcesOnSpecificPages || IsWorkbookDatasourceSubspaceTab(this.props.spaceInfo);
  };

  private dataSourceViewTypeDropdown = (disabled: boolean): JSX.Element => {
    if (this.showDatasourceViewTypeDropDown()) {
      return (
        <React.Fragment>
          <span className={ExplorerStyles.toolbarDropdownLabelClass(disabled)}>{Messages.dataSourceViewTypeDropdown_label()}</span>
          <DatasourceViewTypeDropdown
            disabled={disabled}
            viewTypeSelected={this.props.datasourceViewTypeSelected}
            viewType={this.props.contentFilter[0] as DatasourceViewType} />
        </React.Fragment>
      );
    }

    return null;
  };

  public changeViewMode = (viewMode: DisplayMode): void => {
    this.props.changeViewMode(viewMode);
  };
}

interface DerivedPropsThatDependOnSelectedItems {
  selectedItemsString: string;
  selectedItemsArray: IExplorableItemTypeInfo[];
}

function getDerivedPropsFromSelectedItems(selectedItems: ItemsSet<IExplorableItemTypeInfo>): DerivedPropsThatDependOnSelectedItems {

  const selectedItemsArray = _.values(selectedItems);
  const selectedItemsCount = selectedItemsArray.length;

  const selectedItemsString = Messages.selectedItems({
    COUNT: selectedItemsCount,
    COUNT_LOC: Formatters.formatCount(selectedItemsCount)
  });

  return { selectedItemsString, selectedItemsArray };
}

const horizontalLineStyle: React.CSSProperties = {
  border: '0.75px solid ' + applyColor(DesignTokens.color.elevation.border.default.subtle, '#E6E6E6'),
  marginLeft: '16px',
  marginRight: '16px',
  marginBottom: '8px'
};

export namespace ExplorerToolbarTestIds {
  export const SelectedItemsCount = 'selected-items-count';
  export const SelectAllButton = 'tb-select-all';
  export const DeselectAllButton = 'tb-deselect-all';
  export const ActionsMenu = 'actions-menu';
  export const BusySpinner = 'busy';
  export const FilterByTypeDropdown = 'filter-by-type';
  export const FilterByTypeDropdownContentTypeGroup = 'filter-by-type-content-type-group';
  export const FilterByTypeDropdownItem = 'filter-by-type';
  export const FilterByTypeDropdownButtonLabel = 'filter-by-type-button-label';
  export const FilterByTypeSingleSelectButtonLabel = 'filter-by-type-single-select-label';
  export const FilterByTypeSingleSelectButton = 'filter-by-type-single-select';
  export const FilterPanelButton = 'advanced-filter-panel-Button';
  export const ViewModeSwitcher = 'view-mode-toggle-Button';
  export const SortByDropdown = 'content-explorer-sort-by-button-Button';
  export const ExplorerDropdownLabelSuffix = 'popup';
  export const Toolbar = 'explorer-toolbar';
}
